
/*********************************************************************
 *
 * WARNING: This shader class is automatically generated, any manual
 * edits will be lost.
 *
 ********************************************************************/
import { ShaderBase, ShaderUniformsContainer, ShaderAttributesContainer } from '../shader';
import { Texture, CubeMapTexture } from '../../texture';
import { Context } from '../../context/Context';
import { mat3, mat4, vec2, vec3, vec4 } from "gl-matrix";
import { ShaderSourceParser } from '../shader-source-parser';

export type SsaoBlurShaderUniforms = {
	uSsaoMap: WebGLUniformLocation,
	uDepthMap: WebGLUniformLocation,
	uInvProjection: WebGLUniformLocation,
	uDirection: WebGLUniformLocation,
	uTexelSize: WebGLUniformLocation
};

export type SsaoBlurShaderAttributes = {
	aVertexPosition: number,
	vTexCoord: number
};

export type SsaoBlurShaderSettings = string[];

export class SsaoBlurShader extends ShaderBase<SsaoBlurShaderUniforms, SsaoBlurShaderAttributes> {

    private static fragmentShaderSource = `
in vec2 vTexCoord;
out vec4 fragColor;

uniform sampler2D uSsaoMap;
uniform sampler2D uDepthMap;
uniform mat4 uInvProjection;
uniform vec2 uDirection;
uniform vec2 uTexelSize;

// --- begin: world_pos_from_depth.glsl ---
// Reconstruct world / view position from a 24-bit depth sample, screen-space
// UV, and an inverse projection matrix. Replaces an explicit RGBA32F position
// attachment in the G-buffer (~16 bytes/pixel saved).
//
// Reversed-Z: the depth buffer maps near → 1.0 and far → 0.0. Cleared depth
// (= 0.0) means "no geometry was drawn" — callers should discard or treat this
// as background BEFORE calling these functions. The reconstruction itself
// still works at depth == 0.0 (it returns the far-plane point), but the
// result is rarely useful for lighting/AO/decals.
//
// The math is reversal-agnostic: invViewProjection is the inverse of whichever
// projection the depth buffer was rendered with, and the perspective divide
// recovers the correct world position regardless of which side of the NDC z
// range each plane maps to.

// Inputs:
//   depth              — raw [0,1] value from texture(uDepthMap, uv).r
//   uv                 — screen-space UV (vTexCoord, in [0,1])
//   invViewProjection  — inverse of (projection * view) for the current camera
vec3 worldPosFromDepth(float depth, vec2 uv, mat4 invViewProjection) {
	vec4 ndc = vec4(uv * 2.0 - 1.0, depth * 2.0 - 1.0, 1.0);
	vec4 world = invViewProjection * ndc;
	return world.xyz / world.w;
}

// View-space variant — pass invProjection (inverse of camera.projectionMatrix)
// when you only need view space. Skips the worldPos → viewPos round-trip,
// which matters for SSAO that samples 16+ positions per fragment.
vec3 viewPosFromDepth(float depth, vec2 uv, mat4 invProjection) {
	vec4 ndc = vec4(uv * 2.0 - 1.0, depth * 2.0 - 1.0, 1.0);
	vec4 view = invProjection * ndc;
	return view.xyz / view.w;
}

// --- end: world_pos_from_depth.glsl ---

void main(void) {
	float result = 0.0;
	float totalWeight = 0.0;

	float centerDepth = texture(uDepthMap, vTexCoord).r;
	// Reversed-Z: cleared depth == 0.0 means no geometry.
	float centerViewZ = (centerDepth <= 0.0) ? 0.0 : viewPosFromDepth(centerDepth, vTexCoord, uInvProjection).z;

	for (int i = -3; i <= 3; i++) {
		vec2 offset = uDirection * float(i) * uTexelSize;
		vec2 sampleCoord = vTexCoord + offset;
		float sampleAo = texture(uSsaoMap, sampleCoord).r;

		// Depth-aware bilateral weight using view-space Z difference.
		// World-space 3D distance breaks on tilted flat surfaces at distance;
		// view-space depth is constant across flat surfaces regardless of angle.
		float sampleDepth = texture(uDepthMap, sampleCoord).r;
		float sampleViewZ = (sampleDepth <= 0.0) ? 0.0 : viewPosFromDepth(sampleDepth, sampleCoord, uInvProjection).z;
		float depthDiff = abs(centerViewZ - sampleViewZ);
		float depthWeight = 1.0 / (1.0 + depthDiff * depthDiff * 200.0);
		float spatialWeight = 1.0 / (1.0 + float(i * i) * 0.3);
		float weight = depthWeight * spatialWeight;

		result += sampleAo * weight;
		totalWeight += weight;
	}

	fragColor = vec4(vec3(result / totalWeight), 1.0);
}
`;

    private static vertexShaderSource = `
// --- begin: fullscreen_quad.vert.glsl ---
layout(location = 0) in vec2 aVertexPosition;
out vec2 vTexCoord;

void main(void) {
	vTexCoord = aVertexPosition * 0.5 + 0.5;
	gl_Position = vec4(aVertexPosition, 0.0, 1.0);
}
// --- end: fullscreen_quad.vert.glsl ---
`;

    public override get version(): string {
        return '300 es';
    }

    public override get parser(): any {
        return new ShaderSourceParser();
    }

    protected override get warnAttributeMismatch(): boolean {
        return false;
    }

    public uniformsContainer: SsaoBlurShaderUniformsContainer;
    public attributesContainer: SsaoBlurShaderShaderAttributesContainer;

    constructor(ctx: Context, settings: SsaoBlurShaderSettings) {
        super(ctx, SsaoBlurShader.fragmentShaderSource, SsaoBlurShader.vertexShaderSource, settings);
        this.uniformsContainer = new SsaoBlurShaderUniformsContainer(ctx, this);
        this.attributesContainer = new SsaoBlurShaderShaderAttributesContainer(ctx, this);
    }
}

class SsaoBlurShaderUniformsContainer extends ShaderUniformsContainer {
    constructor(ctx: Context, private shader: SsaoBlurShader) {
        super(ctx);
    }

    public setSsaoMap(texture: Texture) : SsaoBlurShaderUniformsContainer {
        this.setTextureValue('uSsaoMap', this.shader.uniforms.uSsaoMap, texture, 'sampler2D');
        return this;
    }

    public setDepthMap(texture: Texture) : SsaoBlurShaderUniformsContainer {
        this.setTextureValue('uDepthMap', this.shader.uniforms.uDepthMap, texture, 'sampler2D');
        return this;
    }

    public setInvProjection(value: mat4 | number[]) : SsaoBlurShaderUniformsContainer {
        this.setValue('uInvProjection', this.shader.uniforms.uInvProjection, value, 'mat4');
        return this;
    }

    public setDirection(value: vec2 | number[]) : SsaoBlurShaderUniformsContainer {
        this.setValue('uDirection', this.shader.uniforms.uDirection, value, 'vec2');
        return this;
    }

    public setTexelSize(value: vec2 | number[]) : SsaoBlurShaderUniformsContainer {
        this.setValue('uTexelSize', this.shader.uniforms.uTexelSize, value, 'vec2');
        return this;
    }

    protected getTextureUniforms() {
        return [
			{ name: 'uSsaoMap', uniform: this.shader.uniforms.uSsaoMap, dataType: 'sampler2D' },
			{ name: 'uDepthMap', uniform: this.shader.uniforms.uDepthMap, dataType: 'sampler2D' }
        ];
    }
}

class SsaoBlurShaderShaderAttributesContainer extends ShaderAttributesContainer {
    constructor(ctx: Context, private shader: SsaoBlurShader) {
        super(ctx);
    }

    public setVertexPosition(settings: { length: number, offset: number, itemSize: number }) : SsaoBlurShaderShaderAttributesContainer {
        const gl = this.gl;
        this.bindFloatAttribute(this.shader.attributes.aVertexPosition, settings.length, settings.offset, settings.itemSize, gl.FLOAT, Float32Array.BYTES_PER_ELEMENT);
        return this;
    }

    public setvTexCoord(settings: { length: number, offset: number, itemSize: number }) : SsaoBlurShaderShaderAttributesContainer {
        const gl = this.gl;
        this.bindFloatAttribute(this.shader.attributes.vTexCoord, settings.length, settings.offset, settings.itemSize, gl.FLOAT, Float32Array.BYTES_PER_ELEMENT);
        return this;
    }
}